import { Action } from '@ngrx/store';
import {
  SalesOrderPeriod,
  TreatmentType,
} from 'src/app/sales-order-period/models/sales-order-period.models';

import {
  CompanySettings,
  CropFieldAdvice,
  CustomerFertilizerStock,
  ExecutedTreatment,
  ExecutedTreatmentWithNitrogenEffectivity,
  FertilizationBalanceSetting,
  FertilizationSetting,
  FertilizationSettingWithIds,
  FertilizationSettingWithName,
  NitrogenCalculationSettingToggles,
  OrganicMatterReport,
  SoilAnalysis,
  Template,
  Treatment,
  TreatmentOrExecutedTreatmentWithNitrogenEffectivity,
  TreatmentWithNitrogenEffectivity,
} from '../../shared/models';
import { RecommendedNutrientQuantityDataWithIds } from '../models/fertilization-result.model';

export enum AdviceActionTypes {
  SetSelectedCropFieldIds = '[Advice] Set selected crop field ids',
  LoadEditingAdvice = '[Advice] Load editing advice',
  UpdateCompanySettings = '[App] Update company settings',
  SetEditingAdvice = '[Advice] Set editing advice',
  GetTemplate = '[Advice] Get template',
  SetTemplate = '[Advice] Set template',
  SelectSchemaSilently = '[Advice] Select schema silently',
  OpenSelectSchemaDialog = '[Advice Open select schema dialog',
  ClearAdvicePending = '[Advice] Clear advice pending',
  ClearAdviceConfirmed = '[Advice] Clear advice confirmed',
  ClearAdviceConfirmedSuccess = '[Advice] Clear advice confirmed success',
  EditTreatment = '[Advice] Edit crop field treatment',
  EditExecutedTreatment = '[Advice] Edit crop field executed treatment',
  EditAidAdvice = '[Advice] Edit aid advice',
  AddTreatment = '[Advice] Add crop field treatment',
  SaveTreatment = '[Advice] Save crop field treatment',
  SaveTreatmentSuccess = '[Advice] Save crop field treatment success',
  CopyTreatment = '[Advice] Copy treatment',
  CopyTreatmentToOtherAdvicesDialogs = '[Advice] Show copy treatment to other advices (dialogs)',
  CopyExecutedTreatmentToOtherAdvicesDialogs = '[Advice] Show copy executed treatment to other advices (dialogs)',
  CopyTreatmentsToOtherAdvices = '[Advice] Copy treatments to other advices',
  CopyExecutedTreatmentsToOtherAdvices = '[Advice] Copy executed treatments to other advices',
  SaveTreatments = '[Advice] Save crop field treatments',
  SaveTreatmentsSuccess = '[Advice] Save crop field treatments success',
  RemoveTreatment = '[Advice] Remove crop field treatment',
  RemoveTreatmentSuccess = '[Advice] Remove crop field treatment success',
  RemoveTreatmentRequest = '[Advice] Remove crop field treatment request',
  UpdatedTreatments = '[Advice] Has updated the treatments',
  GetPreviousYearsAdvice = '[Advice] Get previous years advice',
  SelectPreviousYearsAdvice = '[Advice] Select previous year advice',
  ApplyPreviousYearsAdvice = '[Advice] Apply previous years advice',
  CopyTreatmentsTo = '[Advice] Copy treatments to',
  OpenCopyTreatmentsToDialog = '[Advice] Open copy treatments to dialog',
  CopyTreatments = '[Advice] Copy treatments',
  CopyTreatmentsSuccess = '[Advice] Copy treatments success',
  GetExecutedTreatmentForAdviceId = '[Advice] Get executed treatments for advice id',
  SetExecutedTreatments = '[Advice] Set executed treatments',
  SaveExecutedTreatment = '[Advice] Save executed crop field treatment',
  SaveExecutedTreatmentSuccess = '[Advice] Save executed crop field treatment success',
  RemoveExecutedTreatment = '[Advice] Remove executed crop field treatment',
  RemoveExecutedTreatmentSuccess = '[Advice] Remove executed crop field treatment success',
  RemoveExecutedTreatmentRequest = '[Advice] Remove executed crop field treatment request',
  OpenFertilizationSettingDialog = '[Advice] Open fertilization setting dialog',
  OpenEditAllFertilizationSettingsDialog = '[Advice] Open edit all fertilization settings dialog',
  OpenCompanySettingsDialog = '[Advice] Open company settings dialog',
  GetCustomerFertilizerStock = '[Advice] Get customer fertilizer stock',
  SetCustomerFertilizerStock = '[Advice] Set customer fertilizer stock',
  SaveFertilizationSetting = '[Advice] Save fertilization setting',
  SaveFertilizationSettingSuccess = '[Advice] Save fertilization setting success',
  GetFertilizationSetting = '[Advice] Get fertilization setting',
  SetFertilizationSetting = '[Advice] Set fertilization setting',
  GetFertilizationSettings = '[Advice] Get fertilization settings',
  SetFertilizationSettings = '[Advice] Set fertilization settings',
  GetRecommendedNutrientQuantityByAdviceIds = '[Advice] Get grouped recommended nutrient quantity by advice ids',
  SetRecommendedNutrientQuantityByAdviceIds = '[Advice] Set grouped recommended nutrient quantity by advice ids',
  ChangeExecutedTreatmentType = '[Advice] Change executed treatment type',
  LoadNitrogenCalculationSettingToggles = '[Advice] Load nitrogen setting toggles',
  SetNitrogenCalculationSettingToggles = '[Advice] Set nitrogen setting toggles',
  LoadNitrogenCalculationSettingTogglesByAdvices = '[Advice] Load all nitrogen setting toggles by advices',
  SetNitrogenCalculationSettingTogglesByAdvices = '[Advice] Set all nitrogen setting toggles by advices',
  ToggleAdviceCompleted = '[Advice] Toggle advice completed',
  GetLatestSoilAnalysis = '[Advice] Get latest soil analysis',
  SetLatestSoilAnalysis = '[Advice] Set latest soil analysis',
  SetOrganicMatterReport = '[Advice] Set organic matter report',
  ClearOrganicMatterReport = '[Advice] Clear organic matter report',
  SelectCustomerForImport = '[Advice] Select customer for import',
  GetAdvicesForCustomer = '[Advice] Get advices for customer',
  SelectAdviceForImport = '[Advice] Select advice for import',
  ImportAdviceFromCustomer = '[Advice] Import advice from customer',
  PrefillCropField = '[Advice] Set prefill CropField',
  OpenEditFertilizationBalanceSettingsDialog = '[Advice] Open edit fertilization balance settings dialog',
  TriggerEditFertilizationBalanceSettingsDialog = '[Advice] Trigger edit fertilization balance settings dialog',
  DoneEditingFertilizationBalanceSettings = '[Advice] Done editing fertilization balance settings',
  UpdateExpectedCropYield = '[Advice] Update Expected Crop Yield',
  UpdateExpectedCropYieldSuccess = '[Advice] Update Expected Crop Yield Success',
  UpdateExpectedCropYieldFailure = '[Advice] Update Expected Crop Yield Failure',
}

export class SetSelectedCropFieldIds implements Action {
  readonly type = AdviceActionTypes.SetSelectedCropFieldIds;
  constructor(public readonly cropFieldIds: number[]) {}
}

export class SetEditingAdvice implements Action {
  readonly type = AdviceActionTypes.SetEditingAdvice;
}

export class LoadEditingAdvice implements Action {
  readonly type = AdviceActionTypes.LoadEditingAdvice;
}

export class GetTemplate implements Action {
  readonly type = AdviceActionTypes.GetTemplate;
  constructor(
    public readonly cropYear: number,
    public readonly cropFieldAdvice: CropFieldAdvice,
    public readonly treatmentType: TreatmentType,
  ) {}
}

export class SetTemplate implements Action {
  readonly type = AdviceActionTypes.SetTemplate;
  constructor(public readonly template: Template = null) {}
}

export class SelectSchemaSilently implements Action {
  readonly type = AdviceActionTypes.SelectSchemaSilently;
}

export class OpenSelectSchemaDialog implements Action {
  readonly type = AdviceActionTypes.OpenSelectSchemaDialog;
}

export class ClearAdvicePending implements Action {
  readonly type = AdviceActionTypes.ClearAdvicePending;
  constructor(public readonly treatmentType: TreatmentType) {}
}

export class ClearAdviceConfirmed implements Action {
  readonly type = AdviceActionTypes.ClearAdviceConfirmed;
  constructor(public readonly treatmentType: TreatmentType) {}
}

export class ClearAdviceConfirmedSuccess implements Action {
  readonly type = AdviceActionTypes.ClearAdviceConfirmedSuccess;
}

export class EditTreatment implements Action {
  readonly type = AdviceActionTypes.EditTreatment;
  constructor(
    public readonly treatmentType: TreatmentType,
    public readonly isAnExecutedTreatment = false,
    public readonly treatment: TreatmentOrExecutedTreatmentWithNitrogenEffectivity = {
      id: '',
      name: '',
      objective: '',
      periodId: null,
      aidAdvices: [],
      moment: '',
      pointOfAttention: '',
      frequency: 1,
      areaInHectaresManual: null,
      shouldUseAreaInHectaresManual: false,
      treatmentType: TreatmentType.GBM,
      isAnExecutedTreatment,
    },
  ) {}
}

export class EditExecutedTreatment implements Action {
  readonly type = AdviceActionTypes.EditExecutedTreatment;
  constructor(
    public readonly treatmentType: TreatmentType,
    public readonly treatment: ExecutedTreatmentWithNitrogenEffectivity = {
      id: 0,
      name: '',
      objective: '',
      periodId: null,
      aidAdvices: [],
      moment: '',
      pointOfAttention: '',
      frequency: 1,
      areaInHectaresManual: 0,
      shouldUseAreaInHectaresManual: false,
      treatmentType: TreatmentType.GBM,
      adviceId: 0,
      isAnExecutedTreatment: true,
    },
  ) {}
}

export class AddTreatment implements Action {
  readonly type = AdviceActionTypes.AddTreatment;
  constructor(public readonly treatmentType: TreatmentType) {}
}

export class RemoveTreatment implements Action {
  readonly type = AdviceActionTypes.RemoveTreatment;
  constructor(public readonly treatment: Treatment) {}
}

export class RemoveTreatmentSuccess implements Action {
  readonly type = AdviceActionTypes.RemoveTreatmentSuccess;
}

export class RemoveTreatmentRequest implements Action {
  readonly type = AdviceActionTypes.RemoveTreatmentRequest;

  constructor(public readonly adviceId: number, public readonly treatmentId: string) {}
}

export class UpdatedTreatments implements Action {
  readonly type = AdviceActionTypes.UpdatedTreatments;
}

export class SaveTreatment implements Action {
  readonly type = AdviceActionTypes.SaveTreatment;
  constructor(
    public readonly adviceId: number,
    public readonly treatment: TreatmentWithNitrogenEffectivity,
  ) {}
}

export class SaveTreatmentSuccess implements Action {
  readonly type = AdviceActionTypes.SaveTreatmentSuccess;
  constructor(public readonly treatmentType: TreatmentType) {}
}

export class CopyTreatment implements Action {
  readonly type = AdviceActionTypes.CopyTreatment;
  constructor(public readonly treatment: TreatmentWithNitrogenEffectivity) {}
}

export class CopyTreatmentToOtherAdvicesDialogs implements Action {
  readonly type = AdviceActionTypes.CopyTreatmentToOtherAdvicesDialogs;
  constructor(
    public readonly treatment: TreatmentWithNitrogenEffectivity,
    public readonly treatmentType: TreatmentType,
  ) {}
}

export class CopyTreatmentsToOtherAdvices implements Action {
  readonly type = AdviceActionTypes.CopyTreatmentsToOtherAdvices;
  constructor(
    public readonly sourceCropFieldAdvice: CropFieldAdvice,
    public readonly treatments: TreatmentWithNitrogenEffectivity[],
    public readonly targetCropFieldAdvices: CropFieldAdvice[],
  ) {}
}

export class CopyExecutedTreatmentToOtherAdvicesDialogs implements Action {
  readonly type = AdviceActionTypes.CopyExecutedTreatmentToOtherAdvicesDialogs;
  constructor(
    public readonly treatment: ExecutedTreatmentWithNitrogenEffectivity,
    public readonly treatmentType: TreatmentType,
  ) {}
}

export class CopyExecutedTreatmentsToOtherAdvices implements Action {
  readonly type = AdviceActionTypes.CopyExecutedTreatmentsToOtherAdvices;
  constructor(
    public readonly sourceCropFieldAdvice: CropFieldAdvice,
    public readonly executedTreatments: ExecutedTreatmentWithNitrogenEffectivity[],
    public readonly targetCropFieldAdvices: CropFieldAdvice[],
  ) {}
}

export class SaveTreatments implements Action {
  readonly type = AdviceActionTypes.SaveTreatments;
  constructor(public readonly adviceId: number, public readonly treatments: Treatment[]) {}
}

export class SaveTreatmentsSuccess implements Action {
  readonly type = AdviceActionTypes.SaveTreatmentsSuccess;
}

export class GetPreviousYearsAdvice implements Action {
  readonly type = AdviceActionTypes.GetPreviousYearsAdvice;
}

export class SelectPreviousYearsAdvice implements Action {
  readonly type = AdviceActionTypes.SelectPreviousYearsAdvice;
  constructor(public readonly cropFieldAdvices: CropFieldAdvice[]) {}
}

export class ApplyPreviousYearsAdvice implements Action {
  readonly type = AdviceActionTypes.ApplyPreviousYearsAdvice;
  constructor(
    public readonly sourceCropFieldAdvice: CropFieldAdvice,
    public readonly targetCropFieldAdvice: CropFieldAdvice,
  ) {}
}

export class CopyTreatmentsTo implements Action {
  readonly type = AdviceActionTypes.CopyTreatmentsTo;
  constructor(public readonly treatmentType: TreatmentType) {}
}

export class OpenCopyTreatmentsToDialog implements Action {
  readonly type = AdviceActionTypes.OpenCopyTreatmentsToDialog;
  constructor(
    public readonly copyExecutedTreatments: boolean,
    public readonly treatmentType: TreatmentType,
  ) {}
}

export class CopyTreatments implements Action {
  readonly type = AdviceActionTypes.CopyTreatments;
  constructor(
    public readonly sourceCropFieldAdvice: CropFieldAdvice,
    public readonly targetCropFieldAdvices: CropFieldAdvice[],
    public readonly treatmentType: TreatmentType,
    public readonly copyExecutedTreatments: boolean,
    public readonly askForRemoveExistingTreatmentsConfirmation = false,
  ) {}
}

export class CopyTreatmentsSuccess implements Action {
  readonly type = AdviceActionTypes.CopyTreatmentsSuccess;
}

export class GetExecutedTreatmentForAdviceId implements Action {
  readonly type = AdviceActionTypes.GetExecutedTreatmentForAdviceId;
  constructor(public readonly adviceId: number) {}
}

export class SetExecutedTreatments implements Action {
  readonly type = AdviceActionTypes.SetExecutedTreatments;
  constructor(
    public readonly executedTreatments: ExecutedTreatment[],
    public readonly pastSalesOrderPeriods: SalesOrderPeriod[],
  ) {}
}

export class SaveExecutedTreatment implements Action {
  readonly type = AdviceActionTypes.SaveExecutedTreatment;
  constructor(public readonly executedTreatment: ExecutedTreatmentWithNitrogenEffectivity) {}
}

export class SaveExecutedTreatmentSuccess implements Action {
  readonly type = AdviceActionTypes.SaveExecutedTreatmentSuccess;
}

export class RemoveExecutedTreatment implements Action {
  readonly type = AdviceActionTypes.RemoveExecutedTreatment;

  constructor(public readonly id: number) {}
}

export class RemoveExecutedTreatmentSuccess implements Action {
  readonly type = AdviceActionTypes.RemoveExecutedTreatmentSuccess;
}

export class RemoveExecutedTreatmentRequest implements Action {
  readonly type = AdviceActionTypes.RemoveExecutedTreatmentRequest;

  constructor(public readonly id: number) {}
}

export class SaveFertilizationSetting implements Action {
  readonly type = AdviceActionTypes.SaveFertilizationSetting;
  constructor(public readonly fertilizationSetting: Partial<FertilizationSetting>) {}
}

export class SaveFertilizationSettingWithName implements Action {
  readonly type = AdviceActionTypes.SaveFertilizationSetting;
  constructor(public readonly fertilizationSettings: Partial<FertilizationSettingWithName[]>) {}
}

export class SaveFertilizationSettingSuccess implements Action {
  readonly type = AdviceActionTypes.SaveFertilizationSettingSuccess;
  constructor(public readonly fertilizationSetting?: FertilizationSettingWithIds) {}
}

export class GetFertilizationSetting implements Action {
  readonly type = AdviceActionTypes.GetFertilizationSetting;
  constructor(public readonly adviceId: number) {}
}

export class SetFertilizationSetting implements Action {
  readonly type = AdviceActionTypes.SetFertilizationSetting;
  constructor(public readonly fertilizationSetting?: FertilizationSettingWithIds) {}
}

export class GetFertilizationSettings implements Action {
  readonly type = AdviceActionTypes.GetFertilizationSettings;
}

export class GetCustomerFertilizerStock implements Action {
  readonly type = AdviceActionTypes.GetCustomerFertilizerStock;
}

export class SetCustomerFertilizerStock implements Action {
  readonly type = AdviceActionTypes.SetCustomerFertilizerStock;
  constructor(public readonly customerFertilizerStock?: CustomerFertilizerStock) {}
}

export class SetFertilizationSettings implements Action {
  readonly type = AdviceActionTypes.SetFertilizationSettings;
  constructor(public readonly fertilizationSettings?: FertilizationSettingWithName[]) {}
}

export class OpenFertilizationSettingDialog implements Action {
  readonly type = AdviceActionTypes.OpenFertilizationSettingDialog;
}

export class OpenEditAllFertilizationSettingsDialog implements Action {
  readonly type = AdviceActionTypes.OpenEditAllFertilizationSettingsDialog;
  constructor(public readonly fertilizationSettings?: FertilizationSettingWithIds[]) {}
}

export class OpenCompanySettingsDialog implements Action {
  readonly type = AdviceActionTypes.OpenCompanySettingsDialog;
}

export class GetRecommendedNutrientQuantityByAdviceIds implements Action {
  readonly type = AdviceActionTypes.GetRecommendedNutrientQuantityByAdviceIds;
  constructor(public readonly cropFieldIds: number[], public readonly cropYear: number) {}
}

export class SetRecommendedNutrientQuantityByAdviceIds implements Action {
  readonly type = AdviceActionTypes.SetRecommendedNutrientQuantityByAdviceIds;
  constructor(
    public readonly recommendedNutrientQuantityWithIds: RecommendedNutrientQuantityDataWithIds[],
  ) {}
}

export class ChangeExecutedTreatmentType implements Action {
  readonly type = AdviceActionTypes.ChangeExecutedTreatmentType;
  constructor(
    public readonly treatment: TreatmentOrExecutedTreatmentWithNitrogenEffectivity,
    public readonly adviceId: number,
  ) {}
}
export class LoadNitrogenCalculationSettingToggles implements Action {
  readonly type = AdviceActionTypes.LoadNitrogenCalculationSettingToggles;
  constructor(public readonly adviceId: number) {}
}

export class SetNitrogenCalculationSettingToggles implements Action {
  readonly type = AdviceActionTypes.SetNitrogenCalculationSettingToggles;
  constructor(
    public readonly nitrogenCalculationSettingToggles: NitrogenCalculationSettingToggles,
  ) {}
}

export class LoadNitrogenCalculationSettingTogglesByAdvices implements Action {
  readonly type = AdviceActionTypes.LoadNitrogenCalculationSettingTogglesByAdvices;
  constructor(public readonly adviceIds: number[]) {}
}

export class SetNitrogenCalculationSettingTogglesByAdvices implements Action {
  readonly type = AdviceActionTypes.SetNitrogenCalculationSettingTogglesByAdvices;
  constructor(
    public readonly nitrogenCalculationSettingToggles: NitrogenCalculationSettingToggles[],
  ) {}
}

export class ToggleAdviceCompleted implements Action {
  readonly type = AdviceActionTypes.ToggleAdviceCompleted;
  constructor(public readonly treatmentType: TreatmentType) {}
}

export class GetLatestSoilAnalysis implements Action {
  readonly type = AdviceActionTypes.GetLatestSoilAnalysis;
  constructor(public readonly soilAnalysisIds: number[]) {}
}

export class SetLatestSoilAnalysis implements Action {
  readonly type = AdviceActionTypes.SetLatestSoilAnalysis;
  constructor(public readonly latestSoilAnalysis?: SoilAnalysis) {}
}

export class SetOrganicMatterReport implements Action {
  readonly type = AdviceActionTypes.SetOrganicMatterReport;
  constructor(public readonly organicMatterReport: OrganicMatterReport) {}
}

export class ClearOrganicMatterReport implements Action {
  readonly type = AdviceActionTypes.ClearOrganicMatterReport;
}

export class SelectCustomerForImport implements Action {
  readonly type = AdviceActionTypes.SelectCustomerForImport;
}

export class GetAdvicesForCustomer implements Action {
  readonly type = AdviceActionTypes.GetAdvicesForCustomer;
  constructor(public readonly customerId: number) {}
}

export class SelectAdviceForImport implements Action {
  readonly type = AdviceActionTypes.SelectAdviceForImport;
  constructor(public readonly advices: CropFieldAdvice[], public readonly customerId: number) {}
}

export class ImportAdviceFromCustomer implements Action {
  readonly type = AdviceActionTypes.ImportAdviceFromCustomer;
  constructor(public readonly customerId: number, public readonly adviceId: number) {}
}

export class PrefillCropField implements Action {
  readonly type = AdviceActionTypes.PrefillCropField;
  constructor(public readonly isPrefilling: boolean) {}
}

export class UpdateCompanySettings implements Action {
  readonly type = AdviceActionTypes.UpdateCompanySettings;
  constructor(public readonly companySettings: CompanySettings) {}
}

export class OpenEditFertilizationBalanceSettingsDialog implements Action {
  readonly type = AdviceActionTypes.OpenEditFertilizationBalanceSettingsDialog;
  constructor(
    public readonly fertilizationBalanceSettings: FertilizationBalanceSetting[],
    public readonly headerBusinessRuleViolations: string[],
  ) {}
}

export class TriggerEditFertilizationBalanceSettingsDialog implements Action {
  readonly type = AdviceActionTypes.TriggerEditFertilizationBalanceSettingsDialog;
}

export class DoneEditingFertilizationBalanceSettings implements Action {
  readonly type = AdviceActionTypes.DoneEditingFertilizationBalanceSettings;
}

export class UpdateExpectedCropYield implements Action {
  readonly type = AdviceActionTypes.UpdateExpectedCropYield;
  constructor(
    public readonly adviceId: number,
    public readonly cropYear: number,
    public readonly expectedCropYield: number,
  ) {}
}

export class UpdateExpectedCropYieldSuccess implements Action {
  readonly type = AdviceActionTypes.UpdateExpectedCropYieldSuccess;
  constructor(
    public readonly adviceId: number,
    public readonly expectedCropYield: number,
  ) {}
}

export class UpdateExpectedCropYieldFailure implements Action {
  readonly type = AdviceActionTypes.UpdateExpectedCropYieldFailure;
}

// export the combined type of all actions
export type AdviceActions =
  | SetSelectedCropFieldIds
  | SetTemplate
  | SelectSchemaSilently
  | OpenSelectSchemaDialog
  | ClearAdvicePending
  | ClearAdviceConfirmed
  | ClearAdviceConfirmedSuccess
  | LoadEditingAdvice
  | EditTreatment
  | EditExecutedTreatment
  | AddTreatment
  | RemoveTreatment
  | RemoveTreatmentSuccess
  | RemoveTreatmentRequest
  | UpdatedTreatments
  | SaveTreatment
  | SaveTreatmentSuccess
  | SaveTreatments
  | SaveTreatmentsSuccess
  | CopyTreatment
  | CopyTreatmentToOtherAdvicesDialogs
  | CopyTreatmentsToOtherAdvices
  | GetPreviousYearsAdvice
  | SelectPreviousYearsAdvice
  | ApplyPreviousYearsAdvice
  | CopyTreatmentsTo
  | OpenCopyTreatmentsToDialog
  | CopyTreatments
  | CopyTreatmentsSuccess
  | GetExecutedTreatmentForAdviceId
  | SetExecutedTreatments
  | SaveExecutedTreatment
  | SaveExecutedTreatmentSuccess
  | RemoveExecutedTreatment
  | RemoveExecutedTreatmentSuccess
  | RemoveExecutedTreatmentRequest
  | SaveFertilizationSetting
  | SaveFertilizationSettingSuccess
  | GetFertilizationSetting
  | SetFertilizationSetting
  | GetFertilizationSettings
  | SetFertilizationSettings
  | OpenFertilizationSettingDialog
  | GetRecommendedNutrientQuantityByAdviceIds
  | SetRecommendedNutrientQuantityByAdviceIds
  | ChangeExecutedTreatmentType
  | SetNitrogenCalculationSettingToggles
  | LoadNitrogenCalculationSettingToggles
  | SetNitrogenCalculationSettingTogglesByAdvices
  | LoadNitrogenCalculationSettingTogglesByAdvices
  | ToggleAdviceCompleted
  | GetLatestSoilAnalysis
  | SetLatestSoilAnalysis
  | SetOrganicMatterReport
  | ClearOrganicMatterReport
  | SelectCustomerForImport
  | GetAdvicesForCustomer
  | SelectAdviceForImport
  | ImportAdviceFromCustomer
  | PrefillCropField
  | GetCustomerFertilizerStock
  | SetCustomerFertilizerStock
  | UpdateCompanySettings
  | OpenEditFertilizationBalanceSettingsDialog
  | TriggerEditFertilizationBalanceSettingsDialog
  | DoneEditingFertilizationBalanceSettings
  | UpdateExpectedCropYield
  | UpdateExpectedCropYieldSuccess
  | UpdateExpectedCropYieldFailure;
